import type { CSSProperties, InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react';
import { CpmButton, CpmIconButton, CpmModal } from '../../../components/design-system';

function CloseIcon() {
    return (
        <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
        >
            <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
        </svg>
    );
}

interface ReportsDialogProps {
    open: boolean;
    width: number;
    onClose: () => void;
    children: ReactNode;
    zIndex?: number;
    closeOnBackdrop?: boolean;
    closeOnEscape?: boolean;
    height?: number | string;
}

export function ReportsDialog({
    open,
    width,
    onClose,
    children,
    zIndex = 60,
    closeOnBackdrop = true,
    closeOnEscape = true,
    height,
}: ReportsDialogProps) {
    return (
        <CpmModal
            open={open}
            variant="dialog"
            maxWidth={width}
            zIndex={zIndex}
            height={height}
            onClose={onClose}
            closeOnBackdrop={closeOnBackdrop}
            closeOnEscape={closeOnEscape}
        >
            {children}
        </CpmModal>
    );
}

export function ReportsDialogHeader({
    kicker,
    title,
    subtitle,
    meta,
    onClose,
    kickerExtra,
    titleExtra,
}: {
    kicker: string;
    title: ReactNode;
    subtitle?: ReactNode;
    meta?: ReactNode;
    onClose?: () => void;
    kickerExtra?: ReactNode;
    titleExtra?: ReactNode;
}) {
    return (
        <div
            className="flex items-start shrink-0"
            style={{
                gap: 16,
                padding: '20px 24px 16px',
                borderBottom: '1px solid var(--cpm-border-default)',
                background: '#fff',
            }}
        >
            <div className="min-w-0 flex-1">
                <div className="flex items-center" style={{ gap: 7 }}>
                    <p
                        className="m-0 uppercase"
                        style={{
                            fontSize: 9.5,
                            fontWeight: 800,
                            letterSpacing: '0.08em',
                            color: 'var(--cpm-text-tertiary)',
                        }}
                    >
                        {kicker}
                    </p>
                    {kickerExtra}
                </div>
                <div className="flex items-center flex-wrap" style={{ gap: 10, marginTop: 5 }}>
                    <h2
                        className="m-0"
                        style={{
                            fontSize: 20,
                            fontWeight: 800,
                            letterSpacing: '-0.02em',
                            color: 'var(--cpm-text-primary)',
                        }}
                    >
                        {title}
                    </h2>
                    {titleExtra}
                </div>
                {subtitle ? (
                    <div
                        style={{
                            marginTop: 6,
                            fontSize: 11.5,
                            fontWeight: 600,
                            lineHeight: 1.45,
                            color: 'var(--cpm-text-secondary)',
                            maxWidth: 560,
                        }}
                    >
                        {subtitle}
                    </div>
                ) : null}
                {meta}
            </div>
            {onClose ? (
                <CpmIconButton icon={<CloseIcon />} label="Close" size="md" onClick={onClose} />
            ) : null}
        </div>
    );
}

export function ReportsDialogBody({
    children,
    pad = 'form',
}: {
    children: ReactNode;
    pad?: 'form' | 'picker';
}) {
    return (
        <div
            className="flex-1 min-h-0 overflow-y-auto [overscroll-behavior:contain]"
            style={{
                padding: pad === 'form' ? '20px 24px 28px' : '18px 24px 24px',
                background: '#fff',
            }}
        >
            {children}
        </div>
    );
}

export function ReportsDialogFooter({
    note,
    previewMain,
    previewSub,
    saving,
    savingLabel,
    onCancel,
    cancelLabel = 'Cancel',
    primaryLabel,
    onPrimary,
    primaryDisabled,
    extra,
    leftAction,
}: {
    note?: ReactNode;
    previewMain?: string;
    previewSub?: string;
    saving?: boolean;
    savingLabel?: string;
    onCancel: () => void;
    cancelLabel?: string;
    primaryLabel: string;
    onPrimary: () => void;
    primaryDisabled?: boolean;
    extra?: ReactNode;
    leftAction?: ReactNode;
}) {
    return (
        <div
            className="flex items-center flex-wrap shrink-0"
            style={{
                justifyContent: 'space-between',
                gap: 16,
                padding: '14px 24px',
                borderTop: '1px solid var(--cpm-border-default)',
                background: 'rgba(249, 249, 252, 0.88)',
                backdropFilter: 'blur(14px)',
            }}
        >
            <div className="flex items-center min-w-0 flex-1" style={{ gap: 12 }}>
                {leftAction}
                {saving ? (
                    <ReportsWait label={savingLabel ?? 'Saving…'} />
                ) : previewMain != null ? (
                    <div>
                        <div
                            style={{
                                fontSize: 12.5,
                                fontWeight: 800,
                                color: 'var(--cpm-text-primary)',
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {previewMain}
                        </div>
                        {previewSub ? (
                            <div
                                style={{
                                    marginTop: 2,
                                    fontSize: 11.5,
                                    fontWeight: 600,
                                    lineHeight: 1.45,
                                    color: 'var(--cpm-text-secondary)',
                                }}
                            >
                                {previewSub}
                            </div>
                        ) : null}
                    </div>
                ) : (
                    <div
                        style={{
                            fontSize: 11.5,
                            fontWeight: 600,
                            color: 'var(--cpm-text-secondary)',
                        }}
                    >
                        {note}
                    </div>
                )}
            </div>
            <div className="flex items-center" style={{ gap: 10 }}>
                {extra}
                <CpmButton variant="outline" size="lg" disabled={saving} onClick={onCancel}>
                    {cancelLabel}
                </CpmButton>
                <CpmButton
                    variant="navy"
                    size="lg"
                    disabled={primaryDisabled || saving}
                    isLoading={saving}
                    onClick={onPrimary}
                >
                    {primaryLabel}
                </CpmButton>
            </div>
        </div>
    );
}

export function ReportsWait({ label, gap = 9 }: { label: string; gap?: number }) {
    return (
        <div className="flex items-center" style={{ gap }}>
            <span className="cpm-pulse-dot" />
            <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--cpm-text-secondary)' }}>
                {label}
            </span>
        </div>
    );
}

export function ReportsAlert({
    tone,
    title,
    body,
    reason,
    action,
}: {
    tone: 'success' | 'danger' | 'warning' | 'info';
    title: string;
    body?: ReactNode;
    reason?: string;
    action?: ReactNode;
}) {
    const palette = {
        success: {
            bg: 'var(--cpm-success-bg)',
            border: 'var(--cpm-success-border)',
            text: 'var(--cpm-success-text)',
        },
        danger: {
            bg: 'var(--cpm-shortfall-bg)',
            border: 'var(--cpm-shortfall-border)',
            text: 'var(--cpm-shortfall-text)',
        },
        warning: {
            bg: 'var(--cpm-warning-bg)',
            border: 'var(--cpm-warning-border)',
            text: 'var(--cpm-warning-text)',
        },
        info: {
            bg: 'var(--cpm-info-bg)',
            border: 'var(--cpm-info-border)',
            text: 'var(--cpm-info-text)',
        },
    }[tone];
    const banner = (
        <div
            style={{
                padding: '12px 14px',
                borderRadius: 10,
                background: palette.bg,
                border: `1px solid ${palette.border}`,
            }}
        >
            <p className="m-0" style={{ fontSize: 13, fontWeight: 800, color: palette.text }}>
                {title}
            </p>
            {body || reason ? (
                <div
                    style={{
                        marginTop: 4,
                        fontSize: 12,
                        fontWeight: 600,
                        lineHeight: 1.45,
                        color: 'var(--cpm-text-secondary)',
                    }}
                >
                    {body}
                    {reason ? <div style={{ marginTop: body ? 6 : 0 }}>{reason}</div> : null}
                </div>
            ) : null}
            {action ? <div style={{ marginTop: 10 }}>{action}</div> : null}
        </div>
    );
    return banner;
}

export function ReportsFieldLabel({
    children,
    required,
    hint,
    trailing,
    end,
}: {
    children: ReactNode;
    required?: boolean;
    hint?: ReactNode;
    trailing?: ReactNode;
    end?: ReactNode;
}) {
    return (
        <div
            className="flex items-center"
            style={{ gap: 7, minHeight: 20, marginBottom: 6, width: '100%' }}
        >
            <span
                style={{
                    fontSize: 11.5,
                    fontWeight: 800,
                    color: 'var(--cpm-text-primary)',
                }}
            >
                {children}
            </span>
            {required ? (
                <span
                    className="uppercase"
                    style={{
                        fontSize: 9.5,
                        fontWeight: 800,
                        letterSpacing: '0.06em',
                        color: 'var(--cpm-error)',
                    }}
                >
                    Required
                </span>
            ) : null}
            {hint ? (
                <span
                    style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--cpm-text-secondary)' }}
                >
                    {hint}
                </span>
            ) : null}
            {trailing}
            {end ? (
                <span
                    style={{
                        marginLeft: 'auto',
                        fontSize: 11.5,
                        fontWeight: 800,
                        color: 'var(--cpm-text-secondary)',
                        fontVariantNumeric: 'tabular-nums',
                    }}
                >
                    {end}
                </span>
            ) : null}
        </div>
    );
}

const INPUT: CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    height: 40,
    padding: '0 12px',
    borderRadius: 10,
    border: '1.5px solid var(--cpm-border-strong)',
    background: '#fff',
    fontFamily: 'var(--cpm-font-sans)',
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--cpm-text-primary)',
    outline: 'none',
};

export function ReportsInput(props: InputHTMLAttributes<HTMLInputElement> & { compact?: boolean }) {
    const { compact, style, ...rest } = props;
    return (
        <input
            {...rest}
            style={{
                ...INPUT,
                height: compact ? 36 : 40,
                ...style,
            }}
        />
    );
}

export function ReportsTextarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
    const { style, ...rest } = props;
    return (
        <textarea
            {...rest}
            style={{
                ...INPUT,
                height: 'auto',
                minHeight: 68,
                padding: '10px 12px',
                resize: 'vertical',
                ...style,
            }}
        />
    );
}

export function ReportsSegmented<T extends string>({
    value,
    options,
    onChange,
    disabled,
    size = 'md',
}: {
    value: T;
    options: { value: T; label: ReactNode }[];
    onChange: (value: T) => void;
    disabled?: boolean;
    size?: 'md' | 'sm';
}) {
    const sm = size === 'sm';
    const many = options.length > 2;
    return (
        <div
            className="flex items-stretch"
            style={{
                width: '100%',
                minWidth: 0,
                height: many ? 'auto' : sm ? 36 : 40,
                minHeight: sm ? 36 : 40,
                padding: 3,
                gap: 3,
                boxSizing: 'border-box',
                borderRadius: 10,
                background: 'var(--cpm-surface-skeleton)',
                border: '1px solid var(--cpm-border-default)',
            }}
        >
            {options.map((option) => {
                const active = option.value === value;
                return (
                    <button
                        key={option.value}
                        type="button"
                        disabled={disabled}
                        onClick={() => onChange(option.value)}
                        style={{
                            flex: '1 1 0',
                            minWidth: 0,
                            height: many ? 'auto' : sm ? 30 : 32,
                            minHeight: sm ? 30 : 32,
                            padding: many ? '5px 6px' : '0 13px',
                            whiteSpace: many ? 'normal' : 'nowrap',
                            textAlign: 'center',
                            lineHeight: many ? 1.2 : undefined,
                            border: 'none',
                            borderRadius: 8,
                            background: active ? '#fff' : 'transparent',
                            color: active ? 'var(--cpm-brand-navy)' : 'var(--cpm-text-secondary)',
                            boxShadow: active ? 'var(--cpm-shadow-pill)' : 'none',
                            fontSize: many ? 11.5 : 12.5,
                            fontWeight: 800,
                            cursor: disabled ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {option.label}
                    </button>
                );
            })}
        </div>
    );
}

export function ReportsHelp({ children }: { children: ReactNode }) {
    return (
        <p
            className="m-0"
            style={{
                marginTop: 6,
                fontSize: 11.5,
                fontWeight: 600,
                lineHeight: 1.45,
                color: 'var(--cpm-text-secondary)',
            }}
        >
            {children}
        </p>
    );
}

export const reportsGrid: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
    gap: '12px 14px',
    alignItems: 'start',
};

export function span(n: number): CSSProperties {
    return { gridColumn: `span ${n}`, minWidth: 0 };
}

export function ReportsSkeletonList({ rows = 5 }: { rows?: number }) {
    return (
        <div
            style={{
                border: '1px solid var(--cpm-border-default)',
                borderRadius: 10,
                overflow: 'hidden',
            }}
        >
            {Array.from({ length: rows }, (_, i) => (
                <div
                    key={i}
                    className="cpm-sk"
                    style={{
                        height: 56,
                        borderRadius: 0,
                        borderBottom:
                            i === rows - 1 ? 'none' : '1px solid var(--cpm-border-default)',
                    }}
                />
            ))}
        </div>
    );
}
